import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { formatLocationDisplay, formatLocationOverride } from '../../utils/locationUtils';
import CollaborationInfoModal from '../CollaborationInfoModal';
import CollaboratorsModal from '../CollaboratorsModal';
import DownloadListButton from '../DownloadListButton';
import ReactivatePremiumModal from '../ReactivatePremiumModal';
import PremiumActionsDropdown from './PremiumActionsDropdown';

// Icon components for cleaner JSX
// All icons use: viewBox="0 0 24 24", strokeLinecap="round", strokeLinejoin="round"
// Sizes: 18px for action buttons, 14px for metadata, 24px for navigation

const EditIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
  </svg>
);

const AddIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <line x1="12" y1="5" x2="12" y2="19"/>
    <line x1="5" y1="12" x2="19" y2="12"/>
  </svg>
);

const DuplicateIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect x="9" y="9" width="13" height="13" rx="2" ry="2"/>
    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>
  </svg>
);

const ImportIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
    <polyline points="14 2 14 8 20 8"/>
    <line x1="9" y1="15" x2="15" y2="15"/>
    <polyline points="12 18 15 15 12 12"/>
  </svg>
);

const ShareIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <circle cx="18" cy="5" r="3"/>
    <circle cx="6" cy="12" r="3"/>
    <circle cx="18" cy="19" r="3"/>
    <line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/>
    <line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/>
  </svg>
);

const MapIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/>
    <circle cx="12" cy="10" r="3"/>
  </svg>
);

const TripIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
    <line x1="16" y1="2" x2="16" y2="6"/>
    <line x1="8" y1="2" x2="8" y2="6"/>
    <line x1="3" y1="10" x2="21" y2="10"/>
    <polyline points="9 16 11 18 15 14"/>
  </svg>
);

// Follow list icon: document with plus (unfollowed), checkmark (following), or minus (hover when following)
const FollowListIcon = ({ filled }) => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className="follow-list-icon">
    <path className="follow-icon-doc" d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
    <polyline points="14 2 14 8 20 8" />
    {filled ? (
      <>
        {/* Checkmark - shown by default when following */}
        <path className="follow-icon-check" d="M9 15l2 2 4-4" />
        {/* Minus - shown on hover when following */}
        <line className="follow-icon-minus" x1="9" y1="14" x2="15" y2="14" />
      </>
    ) : (
      <>
        <line x1="12" y1="11" x2="12" y2="17" />
        <line x1="9" y1="14" x2="15" y2="14" />
      </>
    )}
  </svg>
);

const BellIcon = ({ muted }) => (
  muted ? (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M13.73 21a2 2 0 0 1-3.46 0"/>
      <path d="M18.63 13A17.89 17.89 0 0 1 18 8"/>
      <path d="M6.26 6.26A5.86 5.86 0 0 0 6 8c0 7-3 9-3 9h14"/>
      <path d="M18 8a6 6 0 0 0-9.33-5"/>
      <line x1="1" y1="1" x2="23" y2="23"/>
    </svg>
  ) : (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/>
      <path d="M13.73 21a2 2 0 0 1-3.46 0"/>
    </svg>
  )
);

const LoadingIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <circle cx="12" cy="12" r="10" opacity="0.3"/>
  </svg>
);

const LocationIcon = () => (
  <svg className="location-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/>
    <circle cx="12" cy="10" r="3"/>
  </svg>
);

const LinkIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/>
    <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/>
  </svg>
);

const LockSmallIcon = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className="inactive-lock-icon">
    <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
    <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
  </svg>
);

// Privacy status icon renderer
const PrivacyStatusIcon = ({ status }) => {
  switch (status) {
    case 'private':
      return (
        <svg className="meta-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
          <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
        </svg>
      );
    case 'public':
      return (
        <svg className="meta-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <circle cx="12" cy="12" r="10"/>
          <line x1="2" y1="12" x2="22" y2="12"/>
          <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>
        </svg>
      );
    case 'friends_only':
      return (
        <svg className="meta-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
          <circle cx="12" cy="7" r="4"/>
        </svg>
      );
    case 'shared':
      // Person with lock icon for "Specific Friends" - fits in 24x24 viewBox
      return (
        <svg className="meta-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/>
          <circle cx="9" cy="7" r="4"/>
          <rect x="16" y="13" width="6" height="6" rx="1" ry="1"/>
          <path d="M17 13v-1a2 2 0 0 1 4 0v1"/>
        </svg>
      );
    default:
      return null;
  }
};

const getPrivacyLabel = (status) => {
  switch (status) {
    case 'private': return 'Private';
    case 'public': return 'Public';
    case 'friends_only': return 'Friends Only';
    case 'shared': return 'Specific Friends';
    default: return '';
  }
};

const BackIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <polyline points="15 18 9 12 15 6" />
  </svg>
);

const InfoIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <circle cx="12" cy="12" r="10" />
    <line x1="12" y1="17" x2="12" y2="11" />
    <circle cx="12" cy="8" r="1" fill="currentColor" stroke="none" />
  </svg>
);

const LeaveIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
    <polyline points="16 17 21 12 16 7"/>
    <line x1="21" y1="12" x2="9" y2="12"/>
  </svg>
);

// Followers count icon (inline in header-meta)
const FollowersIcon = () => (
  <svg className="meta-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
    <circle cx="9" cy="7" r="4"/>
    <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
    <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
  </svg>
);

function ListDetailHeader({
  list,
  isOwnList,
  isCollaborationList,
  isUserCollaborator,
  isFollowing,
  followLoading,
  notificationsMuted,
  notificationToggleLoading,
  shareToken,
  canPublicShare,
  canViewMap,
  canTripPlanner,
  canOfflineDownload,
  localFollowersCount,
  listId,
  leaveLoading,
  showLeaveConfirm,
  onEditList,
  onAddPlace,
  onDuplicate,
  onImportPlaces,
  onShare,
  onTripPlan,
  onFollowToggle,
  onNotificationToggle,
  onLeaveClick,
  onLeaveConfirm,
  onLeaveCancel,
  onBack,
  onExternalLinkClick,
}) {
  const navigate = useNavigate();
  const [isCollabInfoModalOpen, setIsCollabInfoModalOpen] = useState(false);
  const [isCollaboratorsModalOpen, setIsCollaboratorsModalOpen] = useState(false);
  const [reactivateModalFeature, setReactivateModalFeature] = useState(null);
  const privacyStatus = list.visibility || (list.isPublic ? 'public' : 'private');
  const collaboratorCount = list.collaborators?.length || 0;

  // Check for inactive premium features (owner viewing their own content with expired premium)
  const hasInactiveItinerary = list.hasInactiveItinerary === true;
  const hasInactiveShareToken = list.hasInactiveShareToken === true;
  const hasInactiveExternalLink = list.hasInactiveExternalLink === true;

  // Count active premium features to determine if we should show dropdown
  // Include inactive features (user had them before, data is preserved)
  const activePremiumCount = [
    canPublicShare || hasInactiveShareToken,
    canTripPlanner || hasInactiveItinerary,
    canViewMap,
    canOfflineDownload,
  ].filter(Boolean).length;

  const showPremiumDropdown = activePremiumCount >= 2;

  // Handler for reactivating features from dropdown
  const handleReactivateFeature = (feature) => {
    setReactivateModalFeature(feature);
  };

  // Handlers for inactive premium features
  const handleTripClick = () => {
    if (hasInactiveItinerary) {
      setReactivateModalFeature('trip');
    } else {
      onTripPlan();
    }
  };

  const handleShareClick = () => {
    if (hasInactiveShareToken) {
      setReactivateModalFeature('share');
    } else {
      onShare();
    }
  };

  const handleInactiveLinkClick = () => {
    setReactivateModalFeature('link');
  };

  return (
    <header className="detail-header">
      <div className="header-back-row">
        <button className="back-btn" onMouseDown={onBack} aria-label="Go back">
          <BackIcon />
        </button>
      </div>

      {isOwnList && (
        <div className="header-actions header-actions-grid">
          <button
            className="btn-icon-header"
            onClick={onEditList}
            title="Edit List"
          >
            <EditIcon />
          </button>
          <button
            className="btn-icon-header"
            onClick={onAddPlace}
            title="Add Place"
          >
            <AddIcon />
          </button>
          <button
            className="btn-icon-header"
            onClick={onDuplicate}
            title="Duplicate List"
          >
            <DuplicateIcon />
          </button>
          <button
            className="btn-icon-header"
            onClick={onImportPlaces}
            title="Import Places"
          >
            <ImportIcon />
          </button>

          {/* Show dropdown when 2+ premium features, otherwise show individual buttons */}
          {showPremiumDropdown ? (
            <PremiumActionsDropdown
              listId={listId}
              listTitle={list.title || list.name}
              list={list}
              shareToken={shareToken}
              canPublicShare={canPublicShare}
              canViewMap={canViewMap}
              canTripPlanner={canTripPlanner}
              canOfflineDownload={canOfflineDownload}
              hasInactiveShareToken={hasInactiveShareToken}
              hasInactiveItinerary={hasInactiveItinerary}
              onShare={onShare}
              onTripPlan={onTripPlan}
              onReactivateFeature={handleReactivateFeature}
            />
          ) : (
            <>
              {/* Share button - only show if user has access or had inactive token */}
              {(canPublicShare || hasInactiveShareToken) && (
                <button
                  className={`btn-icon-header ${(shareToken || hasInactiveShareToken) ? 'active' : ''} ${hasInactiveShareToken ? 'inactive-premium' : ''}`}
                  onClick={handleShareClick}
                  title={hasInactiveShareToken ? 'Share link saved (inactive)' : (shareToken ? 'Manage share link' : 'Share list publicly')}
                >
                  <ShareIcon />
                  {hasInactiveShareToken && <LockSmallIcon />}
                </button>
              )}
              {/* Trip button - only show if user has access or had inactive itinerary */}
              {(canTripPlanner || hasInactiveItinerary) && (
                <button
                  className={`btn-icon-header ${(list.itinerary || hasInactiveItinerary) ? 'active' : ''} ${hasInactiveItinerary ? 'inactive-premium' : ''}`}
                  onClick={handleTripClick}
                  title={hasInactiveItinerary ? 'Trip saved (inactive)' : (list.itinerary ? 'Edit trip plan' : 'Plan trip')}
                >
                  <TripIcon />
                  {hasInactiveItinerary && <LockSmallIcon />}
                </button>
              )}
              {/* Map button - only show if user has access */}
              {canViewMap && (
                <button
                  className="btn-icon-header"
                  onClick={() => navigate(`/map?list=${listId}`)}
                  title="View on Map"
                >
                  <MapIcon />
                </button>
              )}
              {/* Download button - only show if user has access */}
              {canOfflineDownload && (
                <DownloadListButton listId={listId} listTitle={list.title || list.name} />
              )}
            </>
          )}
        </div>
      )}

      {!isOwnList && (
        <div className="header-actions header-actions-grid external-list-actions">
          {isUserCollaborator ? (
            // Collaborator view: Duplicate, Import, Add Place, Premium dropdown/buttons, Share, Leave, Notifications
            <>
              <button
                className="btn-icon-header"
                onClick={onDuplicate}
                title="Duplicate List"
              >
                <DuplicateIcon />
              </button>
              <button
                className="btn-icon-header"
                onClick={onImportPlaces}
                title="Import Places"
              >
                <ImportIcon />
              </button>
              <button
                className="btn-icon-header"
                onClick={onAddPlace}
                title="Add Place"
              >
                <AddIcon />
              </button>

              {/* Premium features - show dropdown if 2+, otherwise individual buttons */}
              {showPremiumDropdown ? (
                <PremiumActionsDropdown
                  listId={listId}
                  listTitle={list.title || list.name}
                  list={list}
                  shareToken={null}
                  canPublicShare={false}
                  canViewMap={canViewMap}
                  canTripPlanner={canTripPlanner}
                  canOfflineDownload={canOfflineDownload}
                  hasInactiveShareToken={false}
                  hasInactiveItinerary={false}
                  onShare={onShare}
                  onTripPlan={onTripPlan}
                  onReactivateFeature={handleReactivateFeature}
                />
              ) : (
                <>
                  {canTripPlanner && (
                    <button
                      className={`btn-icon-header ${list.itinerary ? 'active' : ''}`}
                      onClick={onTripPlan}
                      title={list.itinerary ? 'Edit trip plan' : 'Plan trip'}
                    >
                      <TripIcon />
                    </button>
                  )}
                  {canViewMap && (
                    <button
                      className="btn-icon-header"
                      onClick={() => navigate(`/map?list=${listId}`)}
                      title="View on Map"
                    >
                      <MapIcon />
                    </button>
                  )}
                  {canOfflineDownload && (
                    <DownloadListButton listId={listId} listTitle={list.title || list.name} />
                  )}
                </>
              )}

              <button
                className={`btn-icon-header ${shareToken ? 'active' : ''}`}
                onClick={onShare}
                title={shareToken ? 'Manage share link' : 'Share list'}
              >
                <ShareIcon />
              </button>
              <button
                className="btn-icon-header"
                onClick={onLeaveClick}
                disabled={leaveLoading}
                title="Leave Collaboration"
              >
                {leaveLoading ? <LoadingIcon /> : <LeaveIcon />}
              </button>
              <button
                className={`btn-icon-header ${notificationsMuted ? 'muted' : ''}`}
                onClick={onNotificationToggle}
                disabled={notificationToggleLoading}
                title={notificationsMuted ? 'Notifications muted' : 'Notifications on'}
              >
                {notificationToggleLoading ? <LoadingIcon /> : <BellIcon muted={notificationsMuted} />}
              </button>
            </>
          ) : (
            // Non-collaborator (follower) view: Follow, Notifications, Duplicate, Map
            <>
              <button
                className={`btn-icon-header ${isFollowing ? 'following' : ''}`}
                onClick={onFollowToggle}
                disabled={followLoading}
                title={isFollowing ? 'Following' : 'Follow'}
              >
                {followLoading ? <LoadingIcon /> : <FollowListIcon filled={isFollowing} />}
              </button>
              <button
                className={`btn-icon-header ${!isFollowing ? 'disabled' : ''} ${notificationsMuted ? 'muted' : ''}`}
                onClick={onNotificationToggle}
                disabled={notificationToggleLoading || !isFollowing}
                title={notificationsMuted ? 'Notifications muted' : 'Notifications on'}
              >
                {notificationToggleLoading ? <LoadingIcon /> : <BellIcon muted={notificationsMuted} />}
              </button>
              <button
                className="btn-icon-header"
                onClick={onDuplicate}
                title="Duplicate List"
              >
                <DuplicateIcon />
              </button>
              {/* For followers, only show map if they have access */}
              {canViewMap && (
                <button
                  className="btn-icon-header"
                  onClick={() => navigate(`/map?list=${listId}`)}
                  title="View on Map"
                >
                  <MapIcon />
                </button>
              )}
              {/* For followers, show trip button if the list has a visible itinerary */}
              {list.itinerary && (
                <>
                  {/* Spacer to push trip button to right column */}
                  <div aria-hidden="true" />
                  <button
                    className="btn-icon-header active"
                    onClick={onTripPlan}
                    title="View trip plan"
                  >
                    <TripIcon />
                  </button>
                </>
              )}
            </>
          )}
        </div>
      )}

      <div className="header-title-group">
        <div className="header-title-row">
          <h1>{list.title || list.name}</h1>
          {isCollaborationList && (
            <button
              className="collab-info-btn"
              onClick={() => setIsCollabInfoModalOpen(true)}
              title="About collaboration lists"
            >
              <InfoIcon />
            </button>
          )}
        </div>
        {/* Show creator info for non-own lists, or collaborator count for own collaboration lists */}
        {!isOwnList && list.creatorName && (
          <div
            className={`list-creator-info ${isCollaborationList ? 'collaborative-link' : 'clickable'}`}
            onClick={() => {
              if (isCollaborationList) {
                setIsCollaboratorsModalOpen(true);
              } else {
                navigate(`/user/${list.username || list.creatorId}`);
              }
            }}
          >
            <span>
              by {list.creatorName}
              {isCollaborationList && collaboratorCount > 0 && ` & ${collaboratorCount} other${collaboratorCount === 1 ? '' : 's'}`}
            </span>
            {list.creatorPhotoURL ? (
              <img
                src={list.creatorPhotoURL}
                alt={list.creatorName}
                className="creator-avatar-header"
              />
            ) : (
              <div className="creator-avatar-header creator-avatar-header-placeholder">
                {list.creatorName.charAt(0).toUpperCase()}
              </div>
            )}
          </div>
        )}
        {/* Show collaborator count for owner's view of collaboration lists */}
        {isOwnList && isCollaborationList && collaboratorCount > 0 && (
          <div
            className="list-creator-info collaborative-link"
            onClick={() => setIsCollaboratorsModalOpen(true)}
          >
            <span>
              by You & {collaboratorCount} other{collaboratorCount === 1 ? '' : 's'}
            </span>
          </div>
        )}
      </div>

      {list.description && (
        <div className="header-description">
          {list.description}
        </div>
      )}

      {(list.location || list.locationOverride) && (
        <div className="header-location">
          <LocationIcon />
          <span>
            {formatLocationOverride(list.locationOverride) || formatLocationDisplay(list.location)}
          </span>
          {(list.categoryOverride || list.category) && (
            <>
              <span className="meta-divider">•</span>
              <span>{list.categoryOverride || list.category}</span>
            </>
          )}
        </div>
      )}

      <div className="header-meta">
        <span className="meta-item">
          <PrivacyStatusIcon status={privacyStatus} />
          {getPrivacyLabel(privacyStatus)}
        </span>
        {privacyStatus !== 'private' && (
          <>
            <span className="meta-divider">•</span>
            <span className="meta-item">
              <FollowersIcon />
              {(() => {
                const count = localFollowersCount !== null ? localFollowersCount : (list.followersCount || list.followers || 0);
                return `${count} ${count === 1 ? 'follower' : 'followers'}`;
              })()}
            </span>
          </>
        )}
      </div>

      {list.externalLink?.url && (
        <div className="header-external-link">
          <a
            href={list.externalLink.url}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => onExternalLinkClick?.()}
          >
            <LinkIcon />
            <span>{list.externalLink.title || list.externalLink.url}</span>
          </a>
        </div>
      )}

      {/* Show "Link saved" indicator for inactive external links (owner with expired premium) */}
      {hasInactiveExternalLink && !list.externalLink?.url && (
        <button className="header-external-link-inactive" onClick={handleInactiveLinkClick}>
          <LinkIcon />
          <span>Link saved</span>
          <LockSmallIcon />
        </button>
      )}

      <CollaborationInfoModal
        isOpen={isCollabInfoModalOpen}
        onClose={() => setIsCollabInfoModalOpen(false)}
      />

      <CollaboratorsModal
        isOpen={isCollaboratorsModalOpen}
        onClose={() => setIsCollaboratorsModalOpen(false)}
        listId={listId}
      />

      {/* Leave Collaboration Confirmation Modal */}
      {showLeaveConfirm && (
        <div className="leave-confirm-overlay" onClick={onLeaveCancel}>
          <div className="leave-confirm-dialog" onClick={(e) => e.stopPropagation()}>
            <h3>Leave Collaboration?</h3>
            <p>
              Are you sure you want to leave this collaboration? You will no longer be able to add, edit, or remove places from this list.
            </p>
            <p className="leave-warning">
              The list owner will be notified that you left.
            </p>
            <div className="leave-confirm-actions">
              <button className="btn-cancel" onClick={onLeaveCancel} disabled={leaveLoading}>
                Cancel
              </button>
              <button className="btn-leave" onClick={onLeaveConfirm} disabled={leaveLoading}>
                {leaveLoading ? 'Leaving...' : 'Leave'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reactivate Premium Modal for inactive features */}
      <ReactivatePremiumModal
        isOpen={reactivateModalFeature !== null}
        onClose={() => setReactivateModalFeature(null)}
        featureType={reactivateModalFeature}
      />
    </header>
  );
}

export default ListDetailHeader;
